import { useCallback } from 'react';

import { ANALYTICS_CONFIG } from '../config/analytics';

/**
 * Google Tag Manager event helper.
 *
 * GTM is wired in `gatsby-config.ts` via `gatsby-plugin-google-tagmanager` with a
 * renamed dataLayer (`dataLayerName: 'PageRouteChange'`) and only loads in production
 * (`GATSBY_ENV === 'prod'` + `GATSBY_GTM_ID`). Events must therefore be pushed onto
 * `window.PageRouteChange`, not the default `window.dataLayer`.
 *
 * In non-prod environments GTM is absent; pushes are harmless no-ops that simply
 * accumulate on the array until (if ever) a container reads it.
 */
const DATA_LAYER_NAME = 'PageRouteChange';

type GTMEventPayload = Record<string, unknown> & { event: string };

const pushToDataLayer = (payload: GTMEventPayload): void => {
    // SSR guard: `window` is undefined during Gatsby build.
    if (typeof window === 'undefined') return;

    const win = window as typeof window & { [DATA_LAYER_NAME]?: GTMEventPayload[] };
    win[DATA_LAYER_NAME] = win[DATA_LAYER_NAME] || [];
    win[DATA_LAYER_NAME].push(payload);
};

export const useGTM = () => {
    /**
     * Track a user click on an interactive element.
     *
     * Emits the `user_interaction` dataLayer event carrying a single stable identifier so the GTM
     * container maps every click to one canonical Amplitude event ({@link ANALYTICS_CONFIG.AMPLITUDE.CTA_CLICKED})
     * with the id as its `element_id` property. Callers pass **only** the id — category/label are
     * intentionally not sent, so Amplitude gets one clean event keyed on `element_id`.
     *
     * `mdc_label` is still emitted as a backward-compatible alias for the current container tag;
     * it can be removed once the container reads `element_id` instead.
     *
     * @param id  Stable, kebab-case identifier; becomes `element_id` (e.g. `landing-scan-card-settings`).
     */
    const trackClick = useCallback((id: string): void => {
        pushToDataLayer({
            event: ANALYTICS_CONFIG.EVENTS.USER_INTERACTION,
            // Canonical Amplitude event name — one event for all CTA clicks, mapped 1:1 by the container.
            interaction_event: ANALYTICS_CONFIG.AMPLITUDE.CTA_CLICKED,
            // Single structured property forwarded to Amplitude for segmentation / funnels.
            element_id: id,
            // Backward-compatible alias for the current container tag (reads `mdc_label`).
            mdc_label: id,
        });
    }, []);

    /** Track an arbitrary custom event with an explicit name and payload. */
    const trackCustomEvent = useCallback((event: string, data: Record<string, unknown> = {}): void => {
        pushToDataLayer({ event, ...data });
    }, []);

    /**
     * Track a file submitted for scanning.
     *
     * Pushes the `file_upload` event with the variables the GTM container reads
     * (`file_type`, `file_size`, `success`).
     *
     * @param fileType  MIME type of the uploaded file (e.g. `application/pdf`).
     * @param fileSize  File size in bytes.
     * @param success   Whether the submission succeeded.
     */
    const trackFile = useCallback((fileType: string, fileSize: number, success: boolean): void => {
        pushToDataLayer({
            event: ANALYTICS_CONFIG.EVENTS.FILE_UPLOAD,
            file_type: fileType,
            file_size: fileSize,
            success,
        });
    }, []);

    /**
     * Track a form submission (e.g. a settings form being saved).
     *
     * Pushes the `form_submit` event carrying `form_name` — the GTM container's
     * `DLV - mdc_form_name` reads this and maps it to the Amplitude `feature_name`
     * for the "Feature Used (Form Submit)" tag. Extra fields are spread in as context.
     *
     * @param formName  Stable form identifier; becomes `form_name` (e.g. `settings-profile-save-button`).
     * @param formData  Optional extra context to attach to the event.
     */
    const trackForm = useCallback((formName: string, formData: Record<string, unknown> = {}): void => {
        pushToDataLayer({ event: ANALYTICS_CONFIG.EVENTS.FORM_SUBMIT, form_name: formName, ...formData });
    }, []);

    return { trackClick, trackCustomEvent, trackFile, trackForm };
};
